import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import { toast, type BnColumn, type BnRow } from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError } from "../api/errors.js";
import { listTeams, roleAtLeast, type Team } from "../api/teams.js";
import {
  listCalendars,
  createCalendar,
  updateCalendar,
  deleteCalendar,
  WEEKDAYS,
  type Calendar,
  type Weekday,
} from "../api/calendars.js";

/**
 * page-calendars (/teams/:id/calendars) lists a team's calendars: which
 * weekdays are workdays, and which dates are holidays. A deployment's
 * "workday N" schedule and its window count workdays in the calendar it
 * names, or Monday to Friday without one. A maintainer adds, changes and
 * removes them; a calendar a deployment uses cannot be removed.
 *
 * The routes behind the page exist only while the server has deployments
 * on; the first read tells that apart and the page then shows a notice.
 */
@customElement("page-calendars")
export class PageCalendars extends LitElement {
  @property() teamId = "";

  @state() private team: Team | null = null;
  /** null while deployments are off on the server. */
  @state() private calendars: Calendar[] | null = null;
  @state() private loading = true;
  @state() private error = "";

  /** The calendar being edited; null for a new one. */
  @state() private editing: Calendar | null = null;
  @state() private showForm = false;
  @state() private name = "";
  @state() private workdays: Weekday[] = [];
  @state() private holidays = "";
  @state() private formError = "";
  @state() private saving = false;
  @state() private pendingDelete: Calendar | null = null;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-flex;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      margin-bottom: var(--bino-space-md);
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
      flex-wrap: wrap;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .spacer {
      flex: 1;
    }
    .pad {
      padding: var(--bino-space-md) var(--bino-space-lg);
    }
    .hint {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0;
    }
    .err {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-error);
      margin: 0 0 var(--bino-space-sm);
    }
    .days {
      display: flex;
      gap: var(--bino-space-md);
      flex-wrap: wrap;
    }
    .actions-row {
      display: flex;
      gap: var(--bino-space-sm);
      justify-content: flex-end;
    }
    textarea {
      width: 100%;
      box-sizing: border-box;
      min-height: 8rem;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    textarea:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override updated(changed: PropertyValues) {
    if (changed.has("teamId") && this.teamId) void this.load();
  }

  private async load() {
    const id = this.teamId;
    const stale = () => !this.isConnected || id !== this.teamId;
    this.loading = true;
    this.error = "";
    try {
      const [teams, calendars] = await Promise.all([listTeams(), listCalendars(id)]);
      if (stale()) return;
      this.team = teams.find((t) => t.id === id) ?? null;
      this.calendars = calendars;
      if (!this.team) this.error = "That team does not exist, or you are not a member of it.";
    } catch (e) {
      if (stale()) return;
      this.error = humanError(e);
    } finally {
      if (!stale()) this.loading = false;
    }
  }

  private openForm(cal: Calendar | null) {
    this.editing = cal;
    this.name = cal?.name ?? "";
    this.workdays = cal ? [...cal.workdays] : ["mon", "tue", "wed", "thu", "fri"];
    this.holidays = cal ? cal.holidays.join("\n") : "";
    this.formError = "";
    this.showForm = true;
  }

  private toggleDay(day: Weekday, on: boolean) {
    const set = new Set(this.workdays);
    if (on) set.add(day);
    else set.delete(day);
    this.workdays = WEEKDAYS.map((w) => w.value).filter((w) => set.has(w));
  }

  private async save() {
    const req = {
      name: this.name.trim(),
      workdays: this.workdays,
      holidays: this.holidays
        .split(/[\s,]+/)
        .map((h) => h.trim())
        .filter((h) => h !== ""),
    };
    this.saving = true;
    this.formError = "";
    try {
      if (this.editing) await updateCalendar(this.teamId, this.editing.id, req);
      else await createCalendar(this.teamId, req);
      this.showForm = false;
      toast("Calendar saved", "success");
      this.calendars = await listCalendars(this.teamId);
    } catch (e) {
      this.formError = detailedError(e);
    } finally {
      this.saving = false;
    }
  }

  private async confirmDelete() {
    const cal = this.pendingDelete;
    this.pendingDelete = null;
    if (!cal) return;
    try {
      await deleteCalendar(this.teamId, cal.id);
      this.calendars = (this.calendars ?? []).filter((c) => c.id !== cal.id);
      toast("Calendar removed", "success");
    } catch (e) {
      toast(detailedError(e), "error");
    }
  }

  override render() {
    if (this.loading && !this.team) {
      return html`<bn-spinner block label="Loading calendars…"></bn-spinner>`;
    }
    const backHref = `/teams/${encodeURIComponent(this.teamId)}`;
    const back = () => navigate(backHref);
    if (this.error || !this.team) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load the calendars"
        message=${this.error || "Team not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/teams")}
          >Back to teams</bn-button
        >
      </bn-empty-state>`;
    }
    if (this.calendars === null) {
      return html`<bn-empty-state
        icon="∅"
        heading="No calendars"
        message="Deployments are not enabled on this server."
      >
        <bn-button slot="actions" variant="secondary" @click=${back}>Back to team</bn-button>
      </bn-empty-state>`;
    }
    const canManage = roleAtLeast(this.team.role, "maintainer");
    return html`
      <a
        class="back"
        href=${backHref}
        @click=${(e: Event) => {
          e.preventDefault();
          back();
        }}
        >← ${this.team.displayName || this.team.name}</a
      >
      <div class="head">
        <h1>Calendars</h1>
        <span class="spacer"></span>
        ${canManage
          ? html`<bn-button size="sm" variant="primary" @click=${() => this.openForm(null)}
              >New calendar</bn-button
            >`
          : nothing}
      </div>
      <bn-card heading="Calendars" flush>
        <div class="pad">
          <p class="hint">
            Workday schedules and windows count the workdays of the deployment's calendar. Without
            a calendar, workdays are Monday to Friday with no holidays.
          </p>
        </div>
        <bn-table
          .columns=${[
            { key: "name", label: "Name" },
            { key: "workdays", label: "Workdays" },
            { key: "holidays", label: "Holidays", align: "right", mono: true },
            { key: "actions", label: "", align: "right", width: "1px" },
          ] as BnColumn[]}
          .rows=${this.calendars.map(
            (c): BnRow => ({
              name: c.name,
              workdays: c.workdays.join(", "),
              holidays: String(c.holidays.length),
              actions: canManage
                ? html`<div class="actions-row">
                    <bn-button size="sm" variant="secondary" @click=${() => this.openForm(c)}
                      >Edit</bn-button
                    >
                    <bn-button size="sm" variant="danger" @click=${() => (this.pendingDelete = c)}
                      >Remove</bn-button
                    >
                  </div>`
                : nothing,
            }),
          )}
          empty="No calendars yet."
        ></bn-table>
      </bn-card>

      <bn-modal
        ?open=${this.showForm}
        heading=${this.editing ? "Edit calendar" : "New calendar"}
        @close=${() => (this.showForm = false)}
      >
        ${this.formError ? html`<p class="err">${this.formError}</p>` : nothing}
        <bn-field label="Name" required>
          <bn-input
            .value=${this.name}
            @input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Workdays">
          <div class="days">
            ${WEEKDAYS.map(
              (w) =>
                html`<bn-checkbox
                  label=${w.label}
                  ?checked=${this.workdays.includes(w.value)}
                  @change=${(e: CustomEvent<{ checked: boolean }>) =>
                    this.toggleDay(w.value, e.detail.checked)}
                ></bn-checkbox>`,
            )}
          </div>
        </bn-field>
        <bn-field label="Holidays" hint="One date (YYYY-MM-DD) per line. At most 1000.">
          <textarea
            .value=${this.holidays}
            spellcheck="false"
            @input=${(e: Event) => (this.holidays = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </bn-field>
        <p class="hint">
          When you save, every deployment that uses this calendar plans its next edition again.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showForm = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.saving}
          @click=${() => this.save()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingDelete !== null}
        heading="Remove calendar?"
        @close=${() => (this.pendingDelete = null)}
      >
        <p>
          Remove <strong>${this.pendingDelete?.name}</strong>? A calendar a deployment uses cannot
          be removed; give the deployment another calendar first.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingDelete = null)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmDelete()}
          >Remove</bn-button
        >
      </bn-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-calendars": PageCalendars;
  }
}
